import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';

import useCategoriesWithLandings from 'hooks/useCategoriesWithHighlightedLandings';
import useExperimentVariant from 'hooks/useExperimentVariant';

import { sendEvent } from 'lib/gtag';
import { topbarQueryKeys } from 'lib/queryKeys';

import { useTopbar } from 'providers/TopbarProvider';

import { getTopbarAd } from 'services/ads';

import SitemapGroupList from './SitemapGroupList';

import TOPBAR from 'constants/topbar';

const TopbarAd = dynamic(() => import('./TopbarAd'));
const TopbarDesktop = dynamic(() => import('./Desktop'));
const TopbarMobile = dynamic(() => import('./Mobile'));
const TopbarRedesign = dynamic(() => import('components/TopbarRedesign'));

const Topbar = () => {
  const { isTopbarActive } = useTopbar();
  const [isTopbarRedesignExperimentVariantA] = useExperimentVariant(
    '00022',
    [true, false],
    {
      fallback: { beforeExperimentLoadValue: null, outOfDateValue: true },
    }
  );
  const { categoriesWithHighlightedLandings } = useCategoriesWithLandings();

  const isTopbarRedesignExperimentLoading =
    isTopbarRedesignExperimentVariantA === null;

  const { data: topbarAd } = useQuery({
    queryKey: topbarQueryKeys.bannerAd(),
    queryFn: ({ signal }) => getTopbarAd({ signal }),
    cacheTime: Infinity,
    enabled: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isTopbarActive) {
      sendEvent({
        action: 'header__appear',
        category: TOPBAR.GA_EVENT_CATEGORY,
      });
    }
  }, [isTopbarActive]);

  if (!isTopbarActive) {
    return null;
  }

  return (
    <>
      {topbarAd && <TopbarAd {...topbarAd} />}
      {/* This is used for SEO purposes... */}
      <SitemapGroupList categories={categoriesWithHighlightedLandings} />
      <header className="sticky top-0 z-40 size-full min-h-16 bg-neutral-high-100 transition-all duration-150 ease-in dark:bg-neutral-low-500 lg:h-[116px]">
        {isTopbarRedesignExperimentLoading ? (
          <div className="h-[76px] w-full animate-pulse bg-neutral-high-200 shadow-md dark:bg-neutral-low-500 lg:h-[116px]" />
        ) : isTopbarRedesignExperimentVariantA ? (
          <>
            <TopbarDesktop />
            <TopbarMobile className="lg:hidden" />
          </>
        ) : (
          <TopbarRedesign />
        )}
      </header>
    </>
  );
};

export default Topbar;
